import { HddOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import { looksLikeMediaPath, toWindowsPath } from './mediaPath';

export const ROOT_LABEL = 'Disque média (O:)';

// Barre d'adresse façon explorateur Windows : un fil d'Ariane cliquable qui
// devient un champ de saisie (pour coller O:\Gamme\Produit) au clic à droite.
const AddressBar = ({ path, onNavigate, onPathSubmit, isResolving }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState('');

  // Changer de dossier (double-clic, précédent...) referme la saisie du chemin.
  useEffect(() => {
    setIsEditing(false);
  }, [path]);

  const segments = path ? path.split('/') : [];
  const crumbs = [
    { label: ROOT_LABEL, path: '' },
    ...segments.map((segment, index) => ({
      label: segment,
      path: segments.slice(0, index + 1).join('/'),
    })),
  ];

  const startEditing = () => {
    setText(path ? toWindowsPath(path) : 'O:\\');
    setIsEditing(true);
  };

  const submit = async (value) => {
    if (!value.trim()) return;
    const opened = await onPathSubmit(value);
    if (opened) setIsEditing(false);
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text');
    if (!looksLikeMediaPath(pasted)) return;
    event.preventDefault();
    setText(pasted);
    submit(pasted);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        autoFocus
        aria-label="Chemin du dossier"
        placeholder="O:\Gamme\Produit ou Gamme/Produit"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onPaste={handlePaste}
        onPressEnter={(event) => {
          // Entrée qui valide une saisie IME ne doit pas envoyer le chemin.
          if (event.nativeEvent.isComposing) return;
          submit(text);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => !isResolving && setIsEditing(false)}
        suffix={isResolving ? <LoadingOutlined /> : <span />}
        style={{ flex: 1, fontFamily: 'Consolas, "Courier New", monospace' }}
      />
    );
  }

  return (
    <nav
      aria-label="Chemin du dossier"
      style={{
        flex: 1,
        minWidth: 0,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 4px',
        border: '1px solid #d9d9d9',
        borderRadius: 6,
      }}
    >
      <HddOutlined style={{ margin: '0 6px', color: '#595959' }} />
      {crumbs.map((crumb, index) => (
        <React.Fragment key={crumb.path || 'root'}>
          {index > 0 && (
            <RightOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
          )}
          <Button
            type="text"
            size="small"
            onClick={() => onNavigate(crumb.path)}
            aria-current={index === crumbs.length - 1 ? 'page' : undefined}
            style={{
              fontWeight: index === crumbs.length - 1 ? 600 : 400,
              flexShrink: index === crumbs.length - 1 ? 0 : 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {crumb.label}
          </Button>
        </React.Fragment>
      ))}
      <button
        type="button"
        aria-label="Modifier ou coller un chemin"
        title="Cliquez pour saisir ou coller un chemin (O:\...)"
        onClick={startEditing}
        style={{
          flex: 1,
          alignSelf: 'stretch',
          minWidth: 24,
          border: 0,
          background: 'transparent',
          cursor: 'text',
        }}
      />
    </nav>
  );
};

export default AddressBar;
